import { useQuery } from '@apollo/client/react'
import {
  EmptyState,
  FailureNotice,
  LoadingState,
  PageLayout,
  Section,
  cn,
  useTranslate,
} from '@athena/ui'

import { usePageTitle } from '../app/page-title.js'
import { AvailablePracticeCard } from '../features/learn/AvailablePracticeCard.js'
import { ContinueCard } from '../features/learn/ContinueCard.js'
import { StudentSubjectGroups } from '../features/learn/StudentSubjectGroups.js'
import { useStartPractice } from '../features/learn/useStartPractice.js'
import {
  MyAssignedPracticesDocument,
  type MyAssignedPracticesQuery,
} from '../graphql/generated/operations.js'

type Assigned = MyAssignedPracticesQuery['myAssignedPractices'][number]

/** Unfinished work: an attempt they saved and closed, or simply left open. */
function inProgress(assigned: Assigned): boolean {
  return assigned.latestAttempt?.status === 'IN_PROGRESS'
}

/**
 * The student's home.
 *
 * Two sections in the order a student needs them: what they already started,
 * and what they could start. Unfinished work is first and carries the only
 * primary button on the page — coming back to finish something is the reason
 * to open this screen at all. Below it, subjects with something to start open
 * by themselves and say what is inside when folded (`StudentSubjectGroups`),
 * so the student never has to open every subject to find out whether there
 * is anything to do. How the term has gone is the progress screen's question,
 * and it is reached from the navigation rather than answered here.
 *
 * Which practices can be started is the server's answer, not this screen's.
 * `canStart` and its reason arrive with each row, so the button and the
 * mutation behind it cannot disagree about the rules.
 */
export function AssignedListScreen() {
  const t = useTranslate()
  usePageTitle(t('learn.title'))

  const { data, loading, error, refetch } = useQuery(MyAssignedPracticesDocument)
  const { requestStart, startingId, feedback, dialog } = useStartPractice()
  const start = (row: Assigned) =>
    requestStart({
      practiceId: row.practiceId,
      title: row.title,
      timed: row.timed,
      durationMinutes: row.durationMinutes ?? null,
      inProgress: inProgress(row),
    })

  // Loading says loading, and nothing else: not the empty state, and not a
  // row of folded subjects that would open a moment later.
  if (loading && !data) return <LoadingState>{t('common.loading')}</LoadingState>
  // A home that did not load is not an empty home. Said as the failure it is,
  // with a way to ask again, never as "Nothing to practise yet".
  if (error && !data) {
    return (
      <PageLayout width="operational">
        <FailureNotice
          title={t('learn.loadError')}
          error={error}
          retrying={loading}
          onRetry={() => void refetch()}
        />
      </PageLayout>
    )
  }

  const assigned = data?.myAssignedPractices ?? []
  const continuing = assigned.filter(inProgress)
  const available = assigned
    .filter((row) => !inProgress(row))
    .map((row) => ({
      ...row,
      availableUntil: row.availableUntil ?? null,
      attempted: row.latestAttempt !== null,
    }))
  /** Practices on the shelf, but not one of them can be started now: said so, rather than left to be worked out. */
  const nothingToStart =
    continuing.length === 0 && available.length > 0 && available.every((row) => !row.canStart)

  /**
   * "Nothing assigned" and "nothing assigned yet, but you have finished
   * things before" are different situations, and only the first one means
   * this student has never had anything to do. A student who has cleared
   * everything gets the Available section's "Nothing new to start right now.
   * Well done." rather than an empty state that reads as if they had never
   * been given a practice.
   *
   * One integer decides it. This used to be `myAttempts.length`, which fetched
   * every attempt the student had ever taken, with every field, to choose
   * between two sentences.
   */
  const hasFinishedBefore = (data?.myProgress.totals.attemptCount ?? 0) > 0

  if (assigned.length === 0 && !hasFinishedBefore) {
    return (
      <EmptyState
        variant="page"
        title={t('learn.emptyTitle')}
        description={t('learn.emptyDescription')}
      />
    )
  }

  return (
    <PageLayout width="operational">
      {feedback}

      {continuing.length > 0 ? (
        <Section id="continue" title={t('learn.continueHeading')}>
          {/* One unfinished practice fills the row, two split it in half, and
              three or more take a third each and wrap. A single card at a
              third of the width would look like a leftover rather than the
              thing the student came back to finish. Phones stack either way:
              the row only divides from `md` up, so nothing here shrinks below
              a readable card. */}
          <div
            className={cn(
              'grid gap-4',
              continuing.length === 2 && 'md:grid-cols-2',
              continuing.length >= 3 && 'md:grid-cols-2 desktop:grid-cols-3',
            )}
          >
            {continuing.map((row) => (
              <ContinueCard
                key={row.practiceId}
                practiceTitle={row.title}
                currentQuestionPosition={row.latestAttempt?.currentQuestionPosition ?? null}
                questionTotal={row.latestAttempt?.questionTotal ?? 0}
                resuming={startingId === row.practiceId}
                onResume={() => start(row)}
              />
            ))}
          </div>
        </Section>
      ) : null}

      <Section id="available" title={t('learn.availableHeading')}>
        {available.length === 0 ? (
          <p className="text-body text-text-secondary">{t('learn.nothingNew')}</p>
        ) : (
          <>
            {nothingToStart ? (
              <p className="text-body text-text-secondary">{t('learn.nothingToStart')}</p>
            ) : null}
            <StudentSubjectGroups
              practices={available}
              renderPractice={(row, presentation) => (
                <AvailablePracticeCard
                  key={row.practiceId}
                  title={row.title}
                  subject={row.subject}
                  canStart={row.canStart}
                  startBlockedReason={row.startBlockedReason ?? null}
                  availableUntil={row.availableUntil ?? null}
                  maxAttempts={row.maxAttempts ?? null}
                  attemptsUsed={row.attemptsUsed}
                  timed={row.timed}
                  durationMinutes={row.durationMinutes ?? null}
                  hasBeenAttempted={row.attempted}
                  starting={startingId === row.practiceId}
                  onStart={() => start(row)}
                  presentation={presentation}
                />
              )}
            />
          </>
        )}
      </Section>

      {dialog}
    </PageLayout>
  )
}
